'use strict';

import Modal from 'bootstrap/js/dist/modal';
import VZSpinner from 'Components/VZSpinner';
import { showErrorModal } from 'Root/modal';
import { getParamFromURL, appendParamToURL, getUrlFromAttributes, appendBmHardwareCardIndex } from 'Util/urlUtils';
import { CONST } from 'app_vodafone_ziggo/util/util';

/**
 * @class VZVariationAttribute
 */
class VZVariationAttribute extends HTMLElement {
    constructor() {
        super();
        this.editMode = document.querySelector('.js-edit-mode');
        this.excludedAttributes = ['class', 'data-configurator-type', 'data-url', 'data-general-error'];

        // The variation input should always be the first input inside the vz-variation-attribute element
        this.variationInput = this.querySelector('input');

        this.setupURL = this.setupURL.bind(this);
        this.postUpdate = this.postUpdate.bind(this);
        this.onVariationChange = this.onVariationChange.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.handleAddonClick = this.handleAddonClick.bind(this);
    }

    handleAddonClick(event) {
        event.preventDefault();

        if (this.variationInput) {
            this.variationInput.checked = !this.variationInput.checked;
            this.variationInput.dispatchEvent(new Event('change'));

            const addText = this.addonToggle.querySelector('.js-addon-btn-add');
            const removeText = this.addonToggle.querySelector('.js-addon-btn-remove');

            addText.classList.toggle(CONST.classes.dNone);
            removeText.classList.toggle(CONST.classes.dNone);
            this.classList.toggle('info-card--bordered-primary');
        }
    }

    onVariationChange(evt) {
        if (evt.target === this.variationInput) {
            const url = this.setupURL();
            this.postUpdate(url);
        }
    }

    onQuantityChange() {
        const url = this.setupURL(this.quantityField.value > 0 ? 'data-quantity-url' : 'data-remove-url');
        this.postUpdate(url);
    }

    setupURL(attributeToUse) {
        const { type, checked } = this.variationInput;
        let urlAttributeName = attributeToUse || 'data-url';

        if (!attributeToUse && type === 'checkbox') {
            urlAttributeName = checked ? 'data-add-url' : 'data-remove-url';
        }

        const urlAttributeValue = this.variationInput.getAttribute(urlAttributeName);
        const vZProductCard = this.closest('vz-product-card');

        // Stop in case there's no url to work with (which can happen when the type is checkbox)
        if (urlAttributeValue === 'null' || urlAttributeValue === 'false' || vZProductCard == null) {
            return null;
        }

        let url = getUrlFromAttributes(urlAttributeValue, vZProductCard, this.excludedAttributes);

        // Decode the URI as that's sometimes necessary.
        // There's no hurt in always doing this even if it's not required.
        url = decodeURIComponent(url);

        // Replace placeholders with quantity data
        if (this.quantityField && url.includes('{0}')) {
            const productQty = this.quantityField.value;
            url = url.replace('{0}', `${productQty}`);
        }

        if (this.editMode) {
            const pliId = getParamFromURL(window.location.href, 'pliId');
            url = appendParamToURL(url, 'pliId', pliId);
        }

        if (this.variationInput.name === 'tier' && vZProductCard.getAttribute('data-configurator-type') === 'business-mobile') {
            url = appendParamToURL(url, 'tierChange', true);
        }

        // handle BM Mobile Hardware case, since contractDuration is not a variation attr on the product and businessRuleProduct has pricing and additional info
        if (vZProductCard.getAttribute('data-configurator-type') === 'business-mobile') {
            // Pass along the reversed parameter to change the order of cards
            const isReversed = vZProductCard?.getAttribute('data-reversed') === 'true';
            if (isReversed) {
                url = appendParamToURL(url, 'reversed', isReversed);
            }

            // Calculate the total hardware amount
            const totalHardwareAmount = [...document.querySelectorAll('.js-hardware-phone-wrapper.is-selected .js-quantity-field')].reduce(
                (acc, el) => acc + Number(el.value) || 0,
                0
            );

            // Updated the BM subscription quantity so that it matches the total amount of phones
            const currentQuantity = Number(getParamFromURL(url, 'quantity')) || 0;
            const updatedQuantity = Math.max(currentQuantity, totalHardwareAmount);
            url = appendParamToURL(url, 'quantity', updatedQuantity);

            let businessRulesProducts = vZProductCard.getAttribute('businessrules-products');
            businessRulesProducts = businessRulesProducts && JSON.parse(businessRulesProducts);
            if (!businessRulesProducts) return url;
            Object.keys(businessRulesProducts).forEach((key) => {
                // Only apply changes to the device with matching index
                const hardwareWrapper = this.closest('.js-hardware-phones');
                const deviceIndex = key && key.split('---device')[1];
                const cardIndex = hardwareWrapper && hardwareWrapper.dataset.index;
                if (this.classList.contains('js-hardware-duration-label')) {
                    vZProductCard.querySelectorAll('.js-hardware-duration-label').forEach((el) => el.querySelector('label').classList.remove('label-checked'));
                    if (businessRulesProducts[key].isMobileHardware && deviceIndex === cardIndex) {
                        const priceSlider = hardwareWrapper.querySelector('vz-price-balance-slider');
                        const contractDuration = Number.parseInt(this.querySelector('input')?.value, 10);
                        const netPrice = Number.parseFloat(priceSlider.getAttribute('net-price'), 10);
                        let recurringPrice = Number.parseInt(priceSlider.getAttribute('recurring'), 10);
                        const maxMonthlyCost = Math.min(40, Math.floor(netPrice / (contractDuration + 1)));
                        recurringPrice = Math.min(recurringPrice, maxMonthlyCost);
                        let oneOffPrice = Number.parseFloat((netPrice - contractDuration * recurringPrice).toFixed(2));

                        // Minimum monthly cost is 1 euro with recurring payment
                        if (contractDuration !== -1 && oneOffPrice === netPrice) {
                            recurringPrice = 1;
                            oneOffPrice = netPrice - contractDuration * recurringPrice;
                        }
                        if (contractDuration === -1) {
                            recurringPrice = 0;
                            oneOffPrice = netPrice;
                        }

                        businessRulesProducts[key].contractDuration = contractDuration;
                        businessRulesProducts[key].recurringPrice = recurringPrice;
                        businessRulesProducts[key].oneOffPrice = oneOffPrice;
                    }
                    url = appendParamToURL(url, 'businessrulesproducts', JSON.stringify(businessRulesProducts));
                }
                if (this.classList.contains('js-mobile-hardware-qty')) {
                    if (businessRulesProducts[key].isMobileHardware && deviceIndex === cardIndex) {
                        if (this.quantityField.value > 0) {
                            businessRulesProducts[key].qty = this.quantityField.value;
                        } else {
                            delete businessRulesProducts[key];
                        }
                    }
                    url = appendParamToURL(url, 'businessrulesproducts', JSON.stringify(businessRulesProducts));
                }
                // Remember which BM hardware card to keep open after ajax
                url = appendBmHardwareCardIndex(this, url);
            });
        }
        return url;
    }

    postUpdate(url) {
        if (!url) return;

        this.spinner.start();

        fetch(url)
            .then((res) => res.text())
            .then((response) => {
                this.closest('vz-product-card').dispatchEvent(new CustomEvent('variationChange', { detail: { response: response } }));

                const fragment = document.createElement('template');
                fragment.innerHTML = response;

                document.getElementById('product-configurator').dispatchEvent(new CustomEvent('variationChange'));

                // If in edit mode, and when contract duration has changed, show a modal with extra info
                this.showExtraInfoModal.bind(this);
            })
            .catch((e) => {
                console.log(e);
                const { title, body, cta } = JSON.parse(this.closest('vz-product-card').getAttribute('data-general-error'));
                showErrorModal(title, body, cta);
            })
            .finally(() => {
                this.spinner.stop();
            });
    }

    showExtraInfoModal() {
        const {
            dataset: { displayValue },
            id,
            name
        } = this.variationInput;
        const changeDurationModalElement = document.getElementById('change-duration-modal');
        const configuratorType = this.closest('vz-product-card')?.dataset.configuratorType;
        let isModalRequired = true;

        if (configuratorType === 'business-mobile') {
            // Only query for committed groups that have a duration that's not "monthly"
            const committedGroups = document.querySelectorAll('.js-configurator-summary-committed-group [data-selected-duration]:not([data-selected-duration="1"]');
            const existingDuration = document.querySelector('[data-existing-duration]')?.dataset.existingDuration;
            isModalRequired = committedGroups.length > 0 && id !== '1' && displayValue !== existingDuration;
        }

        if (isModalRequired && name === 'contractDuration' && changeDurationModalElement) {
            const changeDurationModal = new Modal(changeDurationModalElement, { backdrop: 'static', keyboard: false });
            changeDurationModal.show();

            // Dispose and remove the modal when it is closed so it won't be shown again
            changeDurationModalElement.addEventListener('hidden.bs.modal', () => {
                changeDurationModal.dispose();
                changeDurationModalElement.remove();
            });
        }
    }

    connectedCallback() {
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
        this.variationInput.addEventListener('change', this.onVariationChange);

        if (this.classList.contains('js-addon')) {
            this.addonToggle = this.querySelector('.js-addon-toggle');
            if (this.addonToggle) {
                this.addonToggle.addEventListener('click', this.handleAddonClick);
            }
        }

        this.quantityField = this.querySelector('.js-quantity-field');
        if (this.quantityField) {
            this.quantityField.addEventListener('fieldUpdated', this.onQuantityChange);
        }

        // https://stackoverflow.com/questions/62962138/how-to-get-the-contents-of-a-custom-element#answer-62966393
        setTimeout(() => {
            const input = this.querySelector('input[type="radio"]') || this.querySelector('input[type="checkbox"]');
            if (input) {
                input.addEventListener('blur', () => {
                    this.parentElement.classList.remove('focus');
                    [...this.parentElement.children].forEach((child) => {
                        child.removeAttribute('style');
                    });
                });
            }
        });
    }

    disconnectedCallback() {
        this.removeEventListener('change', this.onVariationChange);
    }
}

export default window.customElements.get('vz-variation-attribute') || VZVariationAttribute;

if (!window.customElements.get('vz-variation-attribute')) {
    window.customElements.define('vz-variation-attribute', VZVariationAttribute);
}
